import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Box, ChevronDown, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { DashboardView, MetricKey, MetricRequest, PeriodSpec, ProductItem, ScopeFilters } from '@contracts';
import { fetchMetric } from '@/services/metricService';
import { exportExcel } from '@/lib/exportData';
import { InfoPopover } from '@/components/InfoPopover';
import { LegendPager } from '@/widgets/LegendPager';

// Misma paleta de marca que ChartWidget (categorías múltiples).
const PALETTE = [
  '#dc2626', '#ea580c', '#d97706', '#e11d48', '#f97316', '#be123c',
  '#fb923c', '#f59e0b', '#9f1239', '#db2777', '#7c3aed', '#2563eb',
  '#0891b2', '#059669', '#65a30d', '#9333ea', '#0ea5e9', '#64748b',
];
const OTROS_COLOR = '#94a3b8';
const TOP_CAP = 8;
const LEGEND_PER_PAGE = 6; // filas de leyenda por página (flechas ▲▼, igual que las otras donas)

const fmtInt = (n: number) => Math.round(n).toLocaleString('es-MX');

/** Subtítulo por widget de categoría SB (lo que va junto al título). */
const SUBTITLE: Partial<Record<MetricKey, string>> = {
  productsBreakdown: 'top 8 + otros',
  productsAlmohadas: 'todas las variantes',
  productsTarjets: 'Colchón · Bases · Silla',
  productsRopaCama: 'sábanas, colcha, duvet, toallas…',
};
const CATEGORY_METRICS: MetricKey[] = ['productsAlmohadas', 'productsTarjets', 'productsRopaCama'];

export interface SplitBase {
  view: DashboardView;
  period: PeriodSpec;
  companies: string[];
  scope?: ScopeFilters;
  comparePeriod?: PeriodSpec;
}

interface Slice {
  name: string;
  sku?: string;
  qty: number;
  color: string;
  pct: number;
  /** Fila "Otros" expandible: los productos agregados. */
  rest?: ProductItem[];
}

/** MÁS vendidos: top 8 + 'Otros (N)' agregado; % y total sobre el TOTAL completo. */
function topSlices(items: ProductItem[]): { slices: Slice[]; total: number } {
  const sorted = [...items].sort((a, b) => b.qty - a.qty);
  const total = sorted.reduce((a, p) => a + p.qty, 0) || 1;
  const head = sorted.slice(0, TOP_CAP);
  const rest = sorted.slice(TOP_CAP);
  const slices: Slice[] = head.map((p, i) => ({
    name: p.name, sku: p.sku, qty: p.qty, color: PALETTE[i % PALETTE.length], pct: p.qty / total,
  }));
  if (rest.length) {
    const restQty = rest.reduce((a, p) => a + p.qty, 0);
    slices.push({ name: `Otros (${rest.length})`, qty: restQty, color: OTROS_COLOR, pct: restQty / total, rest });
  }
  return { slices, total };
}

/** MENOS vendidos: los 8 de menor venta; % y total sobre ESE subconjunto (la cola). */
function bottomSlices(items: ProductItem[]): { slices: Slice[]; total: number } {
  const sorted = [...items].sort((a, b) => a.qty - b.qty);
  const tail = sorted.slice(0, TOP_CAP);
  const total = tail.reduce((a, p) => a + p.qty, 0) || 1;
  const slices: Slice[] = tail.map((p, i) => ({
    name: p.name, sku: p.sku, qty: p.qty, color: PALETTE[i % PALETTE.length], pct: p.qty / total,
  }));
  return { slices, total };
}

function Donut({ slices, total }: { slices: Slice[]; total: number }) {
  const R = 52;
  const r = 33;
  const cx = 60;
  const cy = 60;
  const x = (a: number, rad: number) => cx + rad * Math.sin(a);
  const y = (a: number, rad: number) => cy - rad * Math.cos(a);
  const drawn = slices.filter((s) => s.qty > 0);

  let acc = 0;
  const arcs = drawn.map((s) => {
    const a0 = (acc / (total || 1)) * Math.PI * 2;
    acc += s.qty;
    const a1 = (acc / (total || 1)) * Math.PI * 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const d = `M${x(a0, R)} ${y(a0, R)} A${R} ${R} 0 ${large} 1 ${x(a1, R)} ${y(a1, R)} L${x(a1, r)} ${y(a1, r)} A${r} ${r} 0 ${large} 0 ${x(a0, r)} ${y(a0, r)} Z`;
    return { d, color: s.color };
  });

  return (
    <svg viewBox="0 0 120 120" className="h-full w-full">
      {drawn.length === 1 ? (
        <circle cx={cx} cy={cy} r={(R + r) / 2} fill="none" stroke={drawn[0].color} strokeWidth={R - r} />
      ) : (
        arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} />)
      )}
      <text x={cx} y={cy - 6} textAnchor="middle" className="fill-slate-400 text-[8px] font-bold uppercase tracking-wide">Total</text>
      <text x={cx} y={cy + 9} textAnchor="middle" className="fill-slate-800 text-[17px] font-black">{fmtInt(total)}</text>
      <text x={cx} y={cy + 20} textAnchor="middle" className="fill-slate-400 text-[7px]">unidades</text>
    </svg>
  );
}

function LegendRow({ s }: { s: Slice }) {
  const [open, setOpen] = useState(false);
  const isOtros = !!s.rest;
  return (
    <li className="text-xs">
      <div
        className={`flex items-center gap-2 ${isOtros ? 'cursor-pointer select-none' : ''}`}
        onClick={isOtros ? () => setOpen((v) => !v) : undefined}
      >
        <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex items-center gap-1 truncate text-slate-700" title={s.name}>
            {s.name}
            {isOtros && <ChevronDown className={`h-3 w-3 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />}
          </div>
          {s.sku && <div className="truncate font-mono text-[10px] text-slate-400">{s.sku}</div>}
        </div>
        <span className="shrink-0 font-bold tabular-nums text-slate-700">{fmtInt(s.qty)}</span>
        <span className="w-12 shrink-0 text-right tabular-nums text-slate-400">{(s.pct * 100).toFixed(1)}%</span>
      </div>
      {isOtros && open && (
        <ul className="mt-1 max-h-40 space-y-0.5 overflow-auto border-l border-slate-200 pl-3">
          {s.rest!.map((p, i) => (
            <li key={i} className="flex items-center gap-2 text-[11px] text-slate-500">
              <span className="min-w-0 flex-1 truncate" title={p.name}>{p.name}{p.sku ? ` · ${p.sku}` : ''}</span>
              <span className="shrink-0 font-semibold tabular-nums">{fmtInt(p.qty)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Column({ label, sub, accent, build, items, loading, isMobile }: {
  label: string;
  sub: string;
  accent: string;
  build: (items: ProductItem[]) => { slices: Slice[]; total: number };
  items: ProductItem[];
  loading: boolean;
  isMobile: boolean;
}) {
  const { slices, total } = useMemo(() => build(items), [build, items]);
  const [page, setPage] = useState(0);
  // En móvil pagina con flechas ▲▼ (como las demás donas); en desktop muestra todo (scroll de la celda).
  const pages = isMobile ? Math.max(1, Math.ceil(slices.length / LEGEND_PER_PAGE)) : 1;
  const p = Math.min(page, pages - 1);
  const shown = isMobile ? slices.slice(p * LEGEND_PER_PAGE, (p + 1) * LEGEND_PER_PAGE) : slices;
  return (
    <div className="min-w-0 flex-1">
      <div className="mb-2 flex items-center gap-2">
        <span className="inline-block h-3 w-1.5 rounded" style={{ background: accent }} />
        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-600">{label}</span>
        <span className="truncate text-[10px] text-slate-400">{sub}</span>
      </div>
      {loading ? (
        <div className="grid h-28 place-items-center text-xs text-slate-400">Cargando…</div>
      ) : slices.length === 0 ? (
        <div className="grid h-28 place-items-center text-xs text-slate-400">Sin datos</div>
      ) : (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-start">
          {/* Móvil: donut arriba centrado; desktop: a la izquierda. */}
          <div className="aspect-square w-40 max-w-[65%] shrink-0 self-center md:w-[46%] md:min-w-[110px] md:max-w-[240px]">
            <Donut slices={slices} total={total} />
          </div>
          {/* Leyenda a todo el ancho (nombres sin truncar) + paginación ▲▼ (LegendPager compartido). */}
          <div className="w-full md:min-w-0 md:flex-1">
            <ul className="space-y-0.5">
              {shown.map((s, i) => <LegendRow key={p * LEGEND_PER_PAGE + i} s={s} />)}
            </ul>
            <LegendPager page={p} pages={pages} onPrev={() => setPage(p - 1)} onNext={() => setPage(p + 1)} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Productos Vendidos partido en dos: MÁS vendidos (top 8 + Otros) y MENOS vendidos
 * (la cola). Una sola consulta al contrato (la métrica del widget, ya enriquecida
 * con nombre + SKU + categoría); el front solo pinta.
 */
export function ProductsSplitWidget({ base, metric, title, info, helpEdit, editMode, editControls }: {
  base: SplitBase;
  metric: MetricKey;
  title: string;
  /** Tooltip de info del widget (descripción + regla maestra). */
  info?: string;
  /** Edición de la ayuda (admin): se pasa tal cual al InfoPopover. */
  helpEdit?: { editValue: string; onSaveHelp: (text: string) => void; editFirst?: boolean };
  editMode?: boolean;
  editControls?: ReactNode;
}) {
  const req: MetricRequest = {
    metric,
    view: base.view,
    period: base.period,
    comparePeriod: base.comparePeriod,
    companies: base.companies,
    scope: base.scope,
  };
  const { data, isLoading } = useQuery({ queryKey: ['metric', req], queryFn: () => fetchMetric(req) });
  const items = (data?.data as ProductItem[] | undefined) ?? [];
  const isCategory = CATEGORY_METRICS.includes(metric);
  const countLabel = isCategory ? 'variantes' : 'productos';

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const doExport = () =>
    void exportExcel('productos-vendidos', items.map((p, i) => ({
      Posición: i + 1, Producto: p.name, SKU: p.sku ?? '', Categoría: p.category ?? '', Unidades: p.qty, Importe: p.total,
    })));

  return (
    <div className={`flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${editMode ? 'cursor-move ring-2 ring-red-300' : ''}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-700">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-rose-600 to-red-700 text-white">
            <Box className="h-4 w-4" />
          </span>
          <span className="truncate">{title}</span>
          {info && <InfoPopover title={title} body={info} {...helpEdit} />}
          {SUBTITLE[metric] && <span className="hidden truncate text-[11px] font-normal text-slate-400 sm:inline">{SUBTITLE[metric]}</span>}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{items.length} {countLabel}</span>
          {editMode ? (
            <span className="no-drag flex gap-1 text-slate-400">{editControls}</span>
          ) : (
            <button onClick={doExport} title="Descargar Excel" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-emerald-600">
              <Download className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-4 md:min-h-0 md:flex-1 md:flex-row md:gap-6 md:overflow-auto">
        <Column label="Más vendidos" sub="top 8 + otros" accent="#dc2626" build={topSlices} items={items} loading={isLoading} isMobile={isMobile} />
        <div className="hidden w-px shrink-0 bg-slate-100 md:block" />
        <Column label="Menos vendidos" sub="cola · baja rotación" accent="#94a3b8" build={bottomSlices} items={items} loading={isLoading} isMobile={isMobile} />
      </div>
    </div>
  );
}
